import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Plus, Check, RefreshCw, X, Search, Users, Clock, AlertTriangle,
  DollarSign, Edit2, RotateCcw, Paperclip, TrendingDown, Upload,
  FileText, ImageIcon, Trash2, Eye, ExternalLink, ChevronDown, Download
} from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { sociosService } from '../../services/sociosService'
import clientesService from '../../services/clientesService'
import toast from 'react-hot-toast'
import { contem } from '../../utils/texto'

const fmt = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const fmtPct = (v) => `${Number(v || 0).toFixed(2)}%`

const fmtDate = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR') : '—')

const fmtBytes = (b) => {
  if (!b) return ''
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
  return `${(b / (1024 * 1024)).toFixed(1)} MB`
}

const STATUS_CONFIG = {
  ativo:     { label: 'Ativo',     cls: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
  pendente:  { label: 'Pendente',  cls: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500' },
  vencido:   { label: 'Vencido',   cls: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  cancelado: { label: 'Cancelado', cls: 'bg-gray-100 text-gray-500', dot: 'bg-gray-400' },
}

const PRAZOS = [
  { label: '3 meses', meses: 3 },
  { label: '6 meses', meses: 6 },
  { label: '12 meses (anual)', meses: 12 },
  { label: '24 meses', meses: 24 },
  { label: 'Personalizado', meses: null },
]

// ── FileUploadArea ────────────────────────────────────────────────────────────
function FileUploadArea({ files, onChange }) {
  const inputRef = useRef()
  const dropRef = useRef()

  const addFiles = (newFiles) => {
    const arr = Array.from(newFiles).filter(f =>
      f.type.startsWith('image/') || f.type === 'application/pdf'
    )
    onChange([...files, ...arr])
  }

  useEffect(() => {
    const onPaste = (e) => {
      if (e.clipboardData?.files?.length) addFiles(e.clipboardData.files)
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  })

  const onDrop = (e) => {
    e.preventDefault()
    dropRef.current?.classList.remove('border-blue-400', 'bg-blue-50')
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files)
  }

  const onDragOver = (e) => {
    e.preventDefault()
    dropRef.current?.classList.add('border-blue-400', 'bg-blue-50')
  }

  const onDragLeave = () => {
    dropRef.current?.classList.remove('border-blue-400', 'bg-blue-50')
  }

  const removeFile = (idx) => onChange(files.filter((_, i) => i !== idx))

  return (
    <div className="space-y-2">
      <div
        ref={dropRef}
        onClick={() => inputRef.current?.click()}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center cursor-pointer transition-colors hover:border-blue-300 hover:bg-blue-50/40"
      >
        <Upload className="w-5 h-5 text-gray-400 mx-auto mb-1" />
        <p className="text-xs text-gray-500">Arraste, clique ou <kbd className="bg-gray-100 rounded px-1">Ctrl+V</kbd> para colar</p>
        <p className="text-[10px] text-gray-400">Imagens ou PDF</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*,application/pdf"
          className="hidden"
          onChange={e => addFiles(e.target.files)}
        />
      </div>
      {files.length > 0 && (
        <div className="space-y-1">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5 text-xs">
              {f.type === 'application/pdf'
                ? <FileText className="w-3.5 h-3.5 text-red-500 shrink-0" />
                : <ImageIcon className="w-3.5 h-3.5 text-blue-500 shrink-0" />}
              <span className="flex-1 truncate text-gray-700">{f.name}</span>
              <span className="text-gray-400">{fmtBytes(f.size)}</span>
              <button type="button" onClick={() => removeFile(i)} className="text-gray-400 hover:text-red-500">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Modal Baixa ───────────────────────────────────────────────────────────────
function ModalBaixa({ sociedade, onClose, onConfirm, loading }) {
  const hoje = new Date().toISOString().split('T')[0]
  const [dataPagamento, setDataPagamento] = useState(
    sociedade.data_pagamento || hoje
  )
  const [files, setFiles] = useState([])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">
            {sociedade.data_pagamento ? 'Corrigir Baixa' : 'Confirmar Baixa'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-sm font-medium text-gray-800">{sociedade.cliente_nome}</p>
            {sociedade.valor && <p className="text-xs text-gray-500 mt-0.5">Valor: {fmt(sociedade.valor)}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Data do Pagamento</label>
            <input
              type="date"
              value={dataPagamento}
              onChange={e => setDataPagamento(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Comprovante (opcional)</label>
            <FileUploadArea files={files} onChange={setFiles} />
          </div>
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            Cancelar
          </button>
          <button
            onClick={() => onConfirm(dataPagamento || hoje, files)}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {loading ? 'Processando...' : 'Confirmar Baixa'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Modal Comprovantes ────────────────────────────────────────────────────────
function ModalComprovantes({ sociedade, orgId, onClose }) {
  const [comprovantes, setComprovantes] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [files, setFiles] = useState([])

  const carregar = useCallback(async () => {
    try {
      setLoading(true)
      const res = await sociosService.listarComprovantes(orgId, sociedade.id)
      setComprovantes(res.data)
    } catch {
      toast.error('Erro ao carregar comprovantes')
    } finally {
      setLoading(false)
    }
  }, [orgId, sociedade.id])

  useEffect(() => { carregar() }, [carregar])

  const handleUpload = async () => {
    if (!files.length) return
    setUploading(true)
    try {
      await Promise.all(files.map(f => sociosService.uploadComprovante(orgId, sociedade.id, f)))
      toast.success('Comprovante(s) enviado(s)!')
      setFiles([])
      carregar()
    } catch {
      toast.error('Erro ao enviar comprovante')
    } finally {
      setUploading(false)
    }
  }

  const handleExcluir = async (comp) => {
    try {
      await sociosService.excluirComprovante(orgId, sociedade.id, comp.id)
      carregar()
    } catch {
      toast.error('Erro ao excluir comprovante')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">Comprovantes — {sociedade.cliente_nome}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <FileUploadArea files={files} onChange={setFiles} />
          {files.length > 0 && (
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {uploading ? 'Enviando...' : `Enviar ${files.length} arquivo(s)`}
            </button>
          )}
          <div className="border-t border-gray-100 pt-4">
            {loading ? (
              <div className="flex justify-center py-4">
                <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : comprovantes.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">Nenhum comprovante anexado</p>
            ) : (
              <div className="space-y-2">
                {comprovantes.map(c => (
                  <div key={c.id} className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2">
                    {c.tipo_arquivo === 'application/pdf'
                      ? <FileText className="w-4 h-4 text-red-500 shrink-0" />
                      : <ImageIcon className="w-4 h-4 text-blue-500 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-700 truncate">{c.nome_arquivo}</p>
                      {c.tamanho && <p className="text-[10px] text-gray-400">{fmtBytes(c.tamanho)}</p>}
                    </div>
                    <a href={c.url} target="_blank" rel="noreferrer" title="Visualizar"
                      className="text-blue-500 hover:text-blue-700 p-1">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                    <button onClick={() => handleExcluir(c)} title="Excluir"
                      className="text-gray-400 hover:text-red-500 p-1">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Modal Renovar ─────────────────────────────────────────────────────────────
function ModalRenovar({ sociedade, onClose, onConfirm, loading }) {
  const [prazoSelecionado, setPrazoSelecionado] = useState(12)
  const [dataCustom, setDataCustom] = useState('')
  const isCustom = prazoSelecionado === null

  const handleConfirm = () => {
    const dados = isCustom && dataCustom
      ? { prazo_meses: 12, data_vencimento_custom: dataCustom }
      : { prazo_meses: prazoSelecionado }
    onConfirm(dados)
  }

  const descricaoPrazo = () => {
    if (isCustom && dataCustom) return `até ${fmtDate(dataCustom)}`
    if (!isCustom) {
      const inicio = sociedade.data_vencimento
        ? new Date(sociedade.data_vencimento + 'T00:00:00')
        : new Date()
      inicio.setDate(inicio.getDate() + 1)
      const fim = new Date(inicio)
      fim.setMonth(fim.getMonth() + prazoSelecionado)
      return `${inicio.toLocaleDateString('pt-BR')} → ${fim.toLocaleDateString('pt-BR')}`
    }
    return '—'
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">Renovar Sociedade</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-sm font-medium text-gray-800">{sociedade.cliente_nome}</p>
            {sociedade.data_vencimento && (
              <p className="text-xs text-gray-500 mt-0.5">Vencimento atual: {fmtDate(sociedade.data_vencimento)}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Prazo da Renovação</label>
            <div className="space-y-2">
              {PRAZOS.map(p => (
                <label key={p.meses ?? 'custom'} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="prazo"
                    checked={prazoSelecionado === p.meses}
                    onChange={() => setPrazoSelecionado(p.meses)}
                    className="text-blue-600"
                  />
                  <span className="text-sm text-gray-700">{p.label}</span>
                </label>
              ))}
            </div>
          </div>

          {isCustom && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Data de Vencimento</label>
              <input
                type="date"
                value={dataCustom}
                onChange={e => setDataCustom(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          )}

          {(prazoSelecionado || dataCustom) && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
              <p className="text-xs font-medium text-blue-700">Novo período:</p>
              <p className="text-xs text-blue-600 mt-0.5">{descricaoPrazo()}</p>
            </div>
          )}
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading || (isCustom && !dataCustom)}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {loading ? 'Renovando...' : 'Renovar'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Modal Desconto ────────────────────────────────────────────────────────────
function ModalDesconto({ sociedade, orgId, onClose }) {
  const [dados, setDados] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    sociosService.desconto(orgId, sociedade.id)
      .then(res => setDados(res.data))
      .catch(() => toast.error('Erro ao calcular desconto'))
      .finally(() => setLoading(false))
  }, [orgId, sociedade.id])

  const papelLabel = (p) => p === 'comprador' ? 'Comprador' : 'Vendedor'
  const papelColor = (p) => p === 'comprador'
    ? 'bg-blue-100 text-blue-700'
    : 'bg-purple-100 text-purple-700'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-800">Desconto por Período</h2>
            {dados && (
              <p className="text-xs text-gray-500 mt-0.5">
                {dados.cliente_nome} · {fmtDate(dados.periodo_inicio)} → {fmtDate(dados.periodo_fim)}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !dados ? (
            <p className="text-sm text-gray-400 text-center py-12">Não foi possível carregar os dados</p>
          ) : (
            <div className="space-y-5">
              {/* Cards resumo */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Lotes no período', valor: dados.total_lotes, sub: `${dados.total_como_comprador} comprador · ${dados.total_como_vendedor} vendedor`, color: 'blue' },
                  { label: 'Volume negociado', valor: fmt(dados.volume_total), color: 'gray' },
                  { label: 'Economia total', valor: fmt(dados.economia_total), color: 'green', destaque: true },
                ].map(c => (
                  <div key={c.label} className={`rounded-xl border p-3 ${c.destaque ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'}`}>
                    <p className={`text-lg font-bold ${c.destaque ? 'text-green-700' : 'text-gray-800'}`}>{c.valor}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{c.label}</p>
                    {c.sub && <p className="text-[10px] text-gray-400 mt-0.5">{c.sub}</p>}
                  </div>
                ))}
              </div>

              {dados.lotes.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <TrendingDown className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Nenhum lote encontrado neste período</p>
                  <p className="text-xs mt-1">O cliente não participou de leilões como sócio neste intervalo</p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-gray-200">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        {['Leilão', 'Data', 'Papel', 'Valor', '% Padrão', '% Real', 'Economia'].map(h => (
                          <th key={h} className="px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {dados.lotes.map((l, i) => (
                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-3 py-2 text-xs text-gray-700 max-w-[180px] truncate">{l.leilao_nome}</td>
                          <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">{fmtDate(l.leilao_data)}</td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${papelColor(l.papel)}`}>
                              {papelLabel(l.papel)}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs font-medium text-gray-800 whitespace-nowrap">{fmt(l.valor_venda_total)}</td>
                          <td className="px-3 py-2 text-xs text-gray-500">{fmtPct(l.pct_padrao)}</td>
                          <td className="px-3 py-2 text-xs text-green-700 font-medium">{fmtPct(l.pct_real)}</td>
                          <td className="px-3 py-2 text-xs font-bold text-green-600 whitespace-nowrap">{fmt(l.economia)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50 border-t border-gray-200">
                      <tr>
                        <td colSpan={6} className="px-3 py-2 text-xs font-semibold text-gray-600 text-right">Total de economia:</td>
                        <td className="px-3 py-2 text-sm font-bold text-green-600">{fmt(dados.economia_total)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 shrink-0">
          <button onClick={onClose} className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Modal Nova Sociedade ──────────────────────────────────────────────────────
function ModalNovaSociedade({ clientes, onClose, onSalvar, loading }) {
  const [form, setForm] = useState({ cliente_id: '', valor: '', data_inicio: '', data_vencimento: '', observacoes: '' })
  const [busca, setBusca] = useState('')
  const [clientesFiltrados, setClientesFiltrados] = useState([])
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  const handleBusca = (valor) => {
    setBusca(valor)
    if (valor.length >= 2) {
      setClientesFiltrados(clientes.filter(c =>
        contem(c.nome, valor.toLowerCase()) ||
        (c.cpf_cnpj && c.cpf_cnpj.includes(valor))
      ).slice(0, 10))
    } else {
      setClientesFiltrados([])
    }
  }

  const selecionarCliente = (c) => {
    setForm(p => ({ ...p, cliente_id: c.id }))
    setBusca(c.nome)
    setClientesFiltrados([])
  }

  const handleSalvar = () => {
    if (!form.cliente_id) return toast.error('Selecione um cliente')
    onSalvar({
      cliente_id: Number(form.cliente_id),
      valor: form.valor ? Number(form.valor) : null,
      data_inicio: form.data_inicio || null,
      data_vencimento: form.data_vencimento || null,
      observacoes: form.observacoes || null,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">Nova Sociedade</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="relative">
            <label className="block text-xs font-medium text-gray-600 mb-1">Cliente *</label>
            <input
              value={busca}
              onChange={e => handleBusca(e.target.value)}
              placeholder="Buscar por nome ou CPF/CNPJ..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
            {clientesFiltrados.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {clientesFiltrados.map(c => (
                  <button key={c.id} onClick={() => selecionarCliente(c)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-gray-50">
                    <span className="font-medium">{c.nome}</span>
                    {c.cpf_cnpj && <span className="text-gray-400 ml-2">({c.cpf_cnpj})</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs text-gray-500 mb-3">Campos de vigência (opcionais — deixe em branco para modo simples)</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Valor da Sociedade</label>
                <input type="number" step="0.01" value={form.valor} onChange={e => set('valor', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="R$ 0,00" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Data Início</label>
                <input type="date" value={form.data_inicio} onChange={e => set('data_inicio', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Data Vencimento</label>
                <input type="date" value={form.data_vencimento} onChange={e => set('data_vencimento', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Observações</label>
            <textarea value={form.observacoes} onChange={e => set('observacoes', e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" rows={2} />
          </div>
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            Cancelar
          </button>
          <button onClick={handleSalvar} disabled={loading}
            className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {loading ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Modal Editar Sociedade ────────────────────────────────────────────────────
function ModalEditarSociedade({ sociedade, onClose, onSalvar, loading }) {
  const [form, setForm] = useState({
    valor: sociedade.valor ? String(sociedade.valor) : '',
    data_inicio: sociedade.data_inicio || '',
    data_vencimento: sociedade.data_vencimento || '',
    observacoes: sociedade.observacoes || ''
  })
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">Editar Sociedade</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-sm font-medium text-gray-800">{sociedade.cliente_nome}</p>
            <p className="text-xs text-gray-500">{sociedade.cliente_cpf_cnpj || 'Sem CPF/CNPJ'}</p>
          </div>
          <div className="border-t border-gray-100 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Valor da Sociedade</label>
                <input type="number" step="0.01" value={form.valor} onChange={e => set('valor', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="R$ 0,00" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Data Início</label>
                <input type="date" value={form.data_inicio} onChange={e => set('data_inicio', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Data Vencimento</label>
                <input type="date" value={form.data_vencimento} onChange={e => set('data_vencimento', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Observações</label>
            <textarea value={form.observacoes} onChange={e => set('observacoes', e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" rows={2} />
          </div>
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            Cancelar
          </button>
          <button onClick={() => onSalvar({
            valor: form.valor ? Number(form.valor) : null,
            data_inicio: form.data_inicio || null,
            data_vencimento: form.data_vencimento || null,
            observacoes: form.observacoes || null,
          })} disabled={loading}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {loading ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Modal Confirmação simples ─────────────────────────────────────────────────
function ModalConfirmacao({ titulo, mensagem, onClose, onConfirm, loading, btnLabel = 'Confirmar', btnClass = 'bg-green-600 hover:bg-green-700' }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">{titulo}</h2>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm text-gray-600">{mensagem}</p>
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            Cancelar
          </button>
          <button onClick={onConfirm} disabled={loading}
            className={`flex-1 px-4 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50 ${btnClass}`}>
            {loading ? 'Processando...' : btnLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Página Principal ──────────────────────────────────────────────────────────
export default function Socios() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [sociedades, setSociedades] = useState([])
  const [resumo, setResumo] = useState({ total_ativos: 0, total_pendentes: 0, total_vencidos: 0, valor_a_receber: 0 })
  const [loading, setLoading] = useState(false)
  const [clientes, setClientes] = useState([])

  const [search, setSearch] = useState('')
  const [statusFiltro, setStatusFiltro] = useState('ativo')
  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)

  const [selecionados, setSelecionados] = useState([])

  const [modalNova, setModalNova] = useState(false)
  const [modalEditar, setModalEditar] = useState(null)
  const [modalBaixa, setModalBaixa] = useState(null)
  const [modalRenovar, setModalRenovar] = useState(null)
  const [modalCancelar, setModalCancelar] = useState(null)
  const [modalEstornar, setModalEstornar] = useState(null)
  const [modalComprovantes, setModalComprovantes] = useState(null)
  const [modalDesconto, setModalDesconto] = useState(null)
  const [modalLote, setModalLote] = useState(null)
  const [loadingAction, setLoadingAction] = useState(false)
  const [loadingXlsx, setLoadingXlsx] = useState(false)

  const carregar = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    try {
      const params = { page, per_page: 20 }
      if (search) params.search = search
      if (statusFiltro) params.status = statusFiltro
      if (dataInicio) params.data_vencimento_inicio = dataInicio
      if (dataFim) params.data_vencimento_fim = dataFim

      const [listRes, resumoRes] = await Promise.all([
        sociosService.listar(orgId, params),
        sociosService.resumo(orgId),
      ])
      setSociedades(listRes.data.data || [])
      setTotal(listRes.data.total || 0)
      setTotalPages(listRes.data.total_pages || 1)
      setResumo(resumoRes.data)
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Módulo Gestão de Sócios não está habilitado')
      } else {
        toast.error('Erro ao carregar sócios')
      }
    } finally {
      setLoading(false)
    }
  }, [orgId, page, search, statusFiltro, dataInicio, dataFim])

  useEffect(() => { carregar() }, [carregar])

  useEffect(() => {
    if (!orgId) return
    clientesService.listarClientes(orgId, 1, 1000).then(res => {
      setClientes(res?.data || res || [])
    }).catch(() => {})
  }, [orgId])

  const [debounceTimer, setDebounceTimer] = useState(null)
  const handleSearch = (valor) => {
    setSearch(valor)
    if (debounceTimer) clearTimeout(debounceTimer)
    setDebounceTimer(setTimeout(() => setPage(1), 400))
  }

  // Ações
  const handleCriar = async (dados) => {
    setLoadingAction(true)
    try {
      await sociosService.criar(orgId, dados)
      toast.success('Sociedade criada!')
      setModalNova(false)
      carregar()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar sociedade')
    } finally {
      setLoadingAction(false)
    }
  }

  const handleEditar = async (dados) => {
    setLoadingAction(true)
    try {
      await sociosService.atualizar(orgId, modalEditar.id, dados)
      toast.success('Sociedade atualizada!')
      setModalEditar(null)
      carregar()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar')
    } finally {
      setLoadingAction(false)
    }
  }

  const handleBaixa = async (dataPagamento, files) => {
    setLoadingAction(true)
    try {
      await sociosService.darBaixa(orgId, modalBaixa.id, { data_pagamento: dataPagamento })
      if (files.length) {
        await Promise.all(files.map(f => sociosService.uploadComprovante(orgId, modalBaixa.id, f)))
      }
      toast.success('Baixa confirmada!')
      setModalBaixa(null)
      carregar()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao dar baixa')
    } finally {
      setLoadingAction(false)
    }
  }

  const handleEstornar = async () => {
    setLoadingAction(true)
    try {
      await sociosService.estornar(orgId, modalEstornar.id)
      toast.success('Baixa estornada!')
      setModalEstornar(null)
      carregar()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao estornar')
    } finally {
      setLoadingAction(false)
    }
  }

  const handleRenovar = async (dados) => {
    setLoadingAction(true)
    try {
      await sociosService.renovar(orgId, modalRenovar.id, dados)
      toast.success('Sociedade renovada para o próximo período!')
      setModalRenovar(null)
      carregar()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao renovar')
    } finally {
      setLoadingAction(false)
    }
  }

  const handleCancelar = async () => {
    setLoadingAction(true)
    try {
      await sociosService.cancelar(orgId, modalCancelar.id)
      toast.success('Sociedade cancelada!')
      setModalCancelar(null)
      carregar()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao cancelar')
    } finally {
      setLoadingAction(false)
    }
  }

  const handleAcaoLote = async () => {
    setLoadingAction(true)
    try {
      await sociosService.acoesLote(orgId, selecionados, modalLote.acao)
      toast.success(`Ação executada em ${selecionados.length} sociedade(s)!`)
      setModalLote(null)
      setSelecionados([])
      carregar()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro na ação em lote')
    } finally {
      setLoadingAction(false)
    }
  }

  const toggleSelecao = (id) =>
    setSelecionados(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])

  const toggleTodos = () =>
    setSelecionados(selecionados.length === sociedades.length ? [] : sociedades.map(s => s.id))

  const filtrarPorCard = (status) => { setStatusFiltro(status); setPage(1) }

  const handleExportarXlsx = async () => {
    if (!orgId) return
    setLoadingXlsx(true)
    try {
      const params = {}
      if (search) params.search = search
      if (statusFiltro) params.status = statusFiltro
      if (dataInicio) params.data_vencimento_inicio = dataInicio
      if (dataFim) params.data_vencimento_fim = dataFim
      const res = await sociosService.exportarXlsx(orgId, params)
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'socios.xlsx'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      toast.success('Planilha exportada com sucesso!')
    } catch {
      toast.error('Erro ao exportar planilha')
    } finally {
      setLoadingXlsx(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-800">Gestão de Sócios</h1>
        <div className="flex items-center gap-2">
          <button onClick={handleExportarXlsx} disabled={loadingXlsx}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
            {loadingXlsx
              ? <RefreshCw className="w-4 h-4 animate-spin" />
              : <Download className="w-4 h-4" />}
            Exportar XLSX
          </button>
          <button onClick={() => setModalNova(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium">
            <Plus className="w-4 h-4" /> Nova Sociedade
          </button>
        </div>
      </div>

      {/* Dashboard Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { status: 'ativo', label: 'Sócios Ativos', valor: resumo.total_ativos, Icon: Users, bg: 'bg-green-100', ic: 'text-green-600' },
          { status: 'pendente', label: 'Pendentes', valor: resumo.total_pendentes, Icon: Clock, bg: 'bg-yellow-100', ic: 'text-yellow-600' },
          { status: 'vencido', label: 'Vencidos', valor: resumo.total_vencidos, Icon: AlertTriangle, bg: 'bg-red-100', ic: 'text-red-600' },
        ].map(c => (
          <button key={c.status} onClick={() => filtrarPorCard(c.status)}
            className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow text-left">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 ${c.bg} rounded-lg flex items-center justify-center`}>
                <c.Icon className={`w-5 h-5 ${c.ic}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{c.valor}</p>
                <p className="text-xs text-gray-500">{c.label}</p>
              </div>
            </div>
          </button>
        ))}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-lg font-bold text-gray-800">{fmt(resumo.valor_a_receber)}</p>
              <p className="text-xs text-gray-500">A Receber</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input value={search} onChange={e => handleSearch(e.target.value)}
              placeholder="Buscar por nome, CPF/CNPJ ou cidade..."
              className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm" />
          </div>
          <select value={statusFiltro} onChange={e => { setStatusFiltro(e.target.value); setPage(1) }}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
            <option value="">Todos</option>
            <option value="ativo">Ativo</option>
            <option value="pendente">Pendente</option>
            <option value="vencido">Vencido</option>
            <option value="cancelado">Cancelado</option>
          </select>
          <input type="date" value={dataInicio} onChange={e => { setDataInicio(e.target.value); setPage(1) }}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm" title="Vencimento a partir de" />
          <input type="date" value={dataFim} onChange={e => { setDataFim(e.target.value); setPage(1) }}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm" title="Vencimento até" />
          {(search || statusFiltro || dataInicio || dataFim) && (
            <button onClick={() => { setSearch(''); setStatusFiltro(''); setDataInicio(''); setDataFim(''); setPage(1) }}
              className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1">
              <X className="w-3 h-3" /> Limpar
            </button>
          )}
        </div>
      </div>

      {/* Barra lote */}
      {selecionados.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-center justify-between">
          <span className="text-sm text-blue-700 font-medium">{selecionados.length} selecionado(s)</span>
          <div className="flex gap-2">
            <button onClick={() => setModalLote({ acao: 'baixa' })}
              className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium">
              Dar Baixa
            </button>
            <button onClick={() => setModalLote({ acao: 'cancelar' })}
              className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-medium">
              Cancelar
            </button>
            <button onClick={() => setSelecionados([])}
              className="px-3 py-1.5 border border-gray-200 text-gray-600 rounded-lg text-xs hover:bg-gray-50">
              Limpar
            </button>
          </div>
        </div>
      )}

      {/* Tabela */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <input type="checkbox" checked={selecionados.length === sociedades.length && sociedades.length > 0}
                        onChange={toggleTodos} className="rounded border-gray-300" />
                    </th>
                    {['Nome', 'CPF/CNPJ', 'Cidade/UF', 'Vigência', 'Valor', 'Pgto', 'Status', 'Ações'].map(h => (
                      <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sociedades.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-12 text-center text-gray-400 text-sm">
                        Nenhuma sociedade encontrada
                      </td>
                    </tr>
                  ) : (
                    sociedades.map(s => {
                      const sc = STATUS_CONFIG[s.status] || STATUS_CONFIG.pendente
                      return (
                        <tr key={s.id} className="hover:bg-gray-50 transition-colors border-b border-gray-100">
                          <td className="px-4 py-3">
                            <input type="checkbox" checked={selecionados.includes(s.id)}
                              onChange={() => toggleSelecao(s.id)} className="rounded border-gray-300" />
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-gray-800">{s.cliente_nome || '—'}</p>
                            {s.fazenda_nome && <p className="text-xs text-gray-400">{s.fazenda_nome}</p>}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{s.cliente_cpf_cnpj || '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {s.cliente_cidade ? `${s.cliente_cidade}/${s.cliente_estado || ''}` : '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                            {s.tem_vigencia ? (
                              <span>{fmtDate(s.data_inicio)} → {fmtDate(s.data_vencimento)}</span>
                            ) : (
                              <span className="text-gray-400 italic text-xs">Sem vigência</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold text-gray-800 whitespace-nowrap">
                            {s.valor ? fmt(s.valor) : '—'}
                          </td>
                          <td className="px-4 py-3">
                            {s.tem_vigencia ? (
                              s.data_pagamento ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-100 text-green-700"
                                  title={`Pago em ${fmtDate(s.data_pagamento)}`}>
                                  ✓ {fmtDate(s.data_pagamento)}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-600">
                                  ✗ Não pago
                                </span>
                              )
                            ) : (
                              <span className="text-[11px] text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${sc.cls}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                              {sc.label}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              {/* Editar */}
                              {s.status !== 'cancelado' && (
                                <button onClick={() => setModalEditar(s)} title="Editar"
                                  className="p-1.5 bg-gray-500 hover:bg-gray-600 text-white rounded-md">
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {/* Dar baixa */}
                              {s.tem_vigencia && (s.status === 'pendente' || s.status === 'vencido' || (s.status === 'ativo' && !s.data_pagamento)) && (
                                <button onClick={() => setModalBaixa(s)} title="Dar Baixa"
                                  className="p-1.5 bg-green-500 hover:bg-green-600 text-white rounded-md">
                                  <Check className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {/* Estornar */}
                              {s.tem_vigencia && s.data_pagamento && (
                                <button onClick={() => setModalEstornar(s)} title="Estornar Baixa"
                                  className="p-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-md">
                                  <RotateCcw className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {/* Comprovantes */}
                              {s.tem_vigencia && (
                                <button onClick={() => setModalComprovantes(s)} title="Comprovantes"
                                  className="p-1.5 bg-indigo-500 hover:bg-indigo-600 text-white rounded-md">
                                  <Paperclip className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {/* Renovar */}
                              {s.tem_vigencia && (s.status === 'ativo' || s.status === 'vencido') && (
                                <button onClick={() => setModalRenovar(s)} title="Renovar"
                                  className="p-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-md">
                                  <RefreshCw className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {/* Desconto */}
                              {s.tem_vigencia && (
                                <button onClick={() => setModalDesconto(s)} title="Ver Desconto"
                                  className="p-1.5 bg-teal-500 hover:bg-teal-600 text-white rounded-md">
                                  <TrendingDown className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {/* Cancelar */}
                              {s.status !== 'cancelado' && (
                                <button onClick={() => setModalCancelar(s)} title="Cancelar"
                                  className="p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-md">
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between">
                <span className="text-sm text-gray-500">{total} registro(s) — Página {page} de {totalPages}</span>
                <div className="flex gap-2">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50">
                    Anterior
                  </button>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50">
                    Próxima
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modais */}
      {modalNova && (
        <ModalNovaSociedade clientes={clientes} onClose={() => setModalNova(false)} onSalvar={handleCriar} loading={loadingAction} />
      )}
      {modalEditar && (
        <ModalEditarSociedade sociedade={modalEditar} onClose={() => setModalEditar(null)} onSalvar={handleEditar} loading={loadingAction} />
      )}
      {modalBaixa && (
        <ModalBaixa
          sociedade={modalBaixa}
          onClose={() => setModalBaixa(null)}
          onConfirm={handleBaixa}
          loading={loadingAction}
        />
      )}
      {modalEstornar && (
        <ModalConfirmacao
          titulo="Estornar Baixa"
          mensagem={`Deseja estornar o pagamento de ${modalEstornar.cliente_nome} registrado em ${fmtDate(modalEstornar.data_pagamento)}? Os comprovantes também serão removidos.`}
          onClose={() => setModalEstornar(null)}
          onConfirm={handleEstornar}
          loading={loadingAction}
          btnLabel="Estornar"
          btnClass="bg-amber-500 hover:bg-amber-600"
        />
      )}
      {modalComprovantes && (
        <ModalComprovantes
          sociedade={modalComprovantes}
          orgId={orgId}
          onClose={() => setModalComprovantes(null)}
        />
      )}
      {modalRenovar && (
        <ModalRenovar
          sociedade={modalRenovar}
          onClose={() => setModalRenovar(null)}
          onConfirm={handleRenovar}
          loading={loadingAction}
        />
      )}
      {modalDesconto && (
        <ModalDesconto
          sociedade={modalDesconto}
          orgId={orgId}
          onClose={() => setModalDesconto(null)}
        />
      )}
      {modalCancelar && (
        <ModalConfirmacao
          titulo="Cancelar Sociedade"
          mensagem={`Tem certeza que deseja cancelar a sociedade de ${modalCancelar.cliente_nome}?`}
          onClose={() => setModalCancelar(null)}
          onConfirm={handleCancelar}
          loading={loadingAction}
          btnLabel="Cancelar Sociedade"
          btnClass="bg-red-600 hover:bg-red-700"
        />
      )}
      {modalLote && (
        <ModalConfirmacao
          titulo={`Ação em Lote: ${modalLote.acao === 'baixa' ? 'Dar Baixa' : 'Cancelar'}`}
          mensagem={`Executar "${modalLote.acao}" em ${selecionados.length} sociedade(s)?`}
          onClose={() => setModalLote(null)}
          onConfirm={handleAcaoLote}
          loading={loadingAction}
          btnLabel="Confirmar"
          btnClass={modalLote.acao === 'baixa' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
        />
      )}
    </div>
  )
}
